// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { Modal, TextInput, TextArea as FinTextArea, StatusBanner, Button, AppIcon, FormRow, Dropdown } from '@loa/ui-components';
import type { PolicySignatoryOverrideDTO, PolicySignatoryOverrideCreateDTO } from '../../types/policyOverride';
import type { Gender, MaritalStatus } from '../../types/client';
import { createOverride, updateOverride, deleteOverride } from '../../api/policyOverrideApi';

const GENDER_OPTIONS = [
  { value: 'MALE', text: 'Male' },
  { value: 'FEMALE', text: 'Female' },
];

const MARITAL_STATUS_OPTIONS = [
  { value: 'SINGLE', text: 'Single' },
  { value: 'DIVORCED', text: 'Divorced' },
  { value: 'WIDOWED', text: 'Widowed' },
  { value: 'MARRIED', text: 'Married' },
  { value: 'CIVIL_PARTNERSHIP', text: 'Civil Partnership' },
  { value: 'SEPARATED', text: 'Separated' },
];

type PolicyOverrideModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  profileId: string;
  policyId: string;
  signatoryId: string;
  policyNumber: string;
  clientName: string;
  existingOverride?: PolicySignatoryOverrideDTO | null;
  clientProfile: {
    firstName: string;
    lastName: string;
    email: string;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    townCity?: string;
    stateProvinceCounty?: string;
    postalCode?: string;
    countryCode?: string;
    country?: string;
    gender?: Gender;
    maritalStatus?: MaritalStatus;
  };
}>;

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const INITIAL_FORM_STATE: PolicySignatoryOverrideCreateDTO = {
  overrideFirstName: '',
  overrideLastName: '',
  overrideEmail: '',
  overrideAddressLine1: '',
  overrideAddressLine2: '',
  overrideAddressLine3: '',
  overrideTownCity: '',
  overrideStateProvinceCounty: '',
  overridePostalCode: '',
  overrideCountryCode: '',
  overrideCountry: '',
  overrideGender: null,
  overrideMaritalStatus: null,
  reason: '',
};

export function PolicyOverrideModal({
  open,
  onClose,
  onSuccess,
  profileId,
  policyId,
  signatoryId,
  policyNumber,
  clientName,
  existingOverride,
  clientProfile,
}: PolicyOverrideModalProps) {
  const [formState, setFormState] = useState<PolicySignatoryOverrideCreateDTO>({ ...INITIAL_FORM_STATE });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const isEditMode = !!existingOverride;

  useEffect(() => {
    if (open) {
      if (existingOverride) {
        setFormState({
          overrideFirstName: existingOverride.overrideFirstName || '',
          overrideLastName: existingOverride.overrideLastName || '',
          overrideEmail: existingOverride.overrideEmail || '',
          overrideAddressLine1: existingOverride.overrideAddressLine1 || '',
          overrideAddressLine2: existingOverride.overrideAddressLine2 || '',
          overrideAddressLine3: existingOverride.overrideAddressLine3 || '',
          overrideTownCity: existingOverride.overrideTownCity || '',
          overrideStateProvinceCounty: existingOverride.overrideStateProvinceCounty || '',
          overridePostalCode: existingOverride.overridePostalCode || '',
          overrideCountryCode: existingOverride.overrideCountryCode || '',
          overrideCountry: existingOverride.overrideCountry || '',
          overrideGender: existingOverride.overrideGender ?? null,
          overrideMaritalStatus: existingOverride.overrideMaritalStatus ?? null,
          reason: existingOverride.reason || '',
        });
      } else {
        setFormState({ ...INITIAL_FORM_STATE });
      }
      setSubmitting(false);
      setError(null);
      setEmailError(null);
      setShowRemoveConfirm(false);
    }
  }, [open, existingOverride]);

  const handleFieldChange = (field: keyof PolicySignatoryOverrideCreateDTO, value: string) => {
    setFormState(prev => ({ ...prev, [field]: value }));
    setError(null);
    if (field === 'overrideEmail') {
      setEmailError(null);
    }
  };

  const handleGenderChange = (value: string) => {
    setFormState(prev => ({ ...prev, overrideGender: value ? (value as Gender) : null }));
    setError(null);
  };

  const handleMaritalStatusChange = (value: string) => {
    setFormState(prev => ({ ...prev, overrideMaritalStatus: value ? (value as MaritalStatus) : null }));
    setError(null);
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= 1000) {
      setFormState(prev => ({ ...prev, reason: text }));
      setError(null);
    }
  };

  const handleEmailBlur = () => {
    const email = formState.overrideEmail?.trim();
    if (email && !isValidEmail(email)) {
      setEmailError('Please enter a valid email address.');
    } else {
      setEmailError(null);
    }
  };

  const buildRequest = (): PolicySignatoryOverrideCreateDTO => ({
    overrideFirstName: formState.overrideFirstName?.trim() || null,
    overrideLastName: formState.overrideLastName?.trim() || null,
    overrideEmail: formState.overrideEmail?.trim() || null,
    overrideAddressLine1: formState.overrideAddressLine1?.trim() || null,
    overrideAddressLine2: formState.overrideAddressLine2?.trim() || null,
    overrideAddressLine3: formState.overrideAddressLine3?.trim() || null,
    overrideTownCity: formState.overrideTownCity?.trim() || null,
    overrideStateProvinceCounty: formState.overrideStateProvinceCounty?.trim() || null,
    overridePostalCode: formState.overridePostalCode?.trim() || null,
    overrideCountryCode: formState.overrideCountryCode?.trim() || null,
    overrideCountry: formState.overrideCountry?.trim() || null,
    overrideGender: formState.overrideGender ?? null,
    overrideMaritalStatus: formState.overrideMaritalStatus ?? null,
    reason: formState.reason.trim(),
  });

  const hasAnyOverrideField = [
    formState.overrideFirstName,
    formState.overrideLastName,
    formState.overrideEmail,
    formState.overrideAddressLine1,
    formState.overrideAddressLine2,
    formState.overrideAddressLine3,
    formState.overrideTownCity,
    formState.overrideStateProvinceCounty,
    formState.overridePostalCode,
    formState.overrideCountryCode,
    formState.overrideCountry,
  ].some(v => v && v.trim() !== '') || !!formState.overrideGender || !!formState.overrideMaritalStatus;

  const trimmedReason = formState.reason.trim();

  const hasInvalidEmail =
    !!formState.overrideEmail?.trim() && !isValidEmail(formState.overrideEmail.trim());

  const isSubmitDisabled =
    submitting ||
    trimmedReason.length < 5 ||
    trimmedReason.length > 1000 ||
    !hasAnyOverrideField ||
    hasInvalidEmail;

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;

    setSubmitting(true);
    setError(null);

    try {
      const request = buildRequest();
      if (isEditMode) {
        await updateOverride(profileId, policyId, signatoryId, request);
      } else {
        await createOverride(profileId, policyId, signatoryId, request);
      }
      onSuccess();
      onClose();
    } catch (err) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
      if (axiosErr.response?.status === 400) {
        setError(axiosErr.response.data?.message || 'Invalid input. Please check your fields and try again.');
      } else if (axiosErr.response?.status === 403) {
        setError('You do not have permission to manage overrides for this profile.');
      } else if (axiosErr.response?.status === 404) {
        setError('Policy signatory not found. Please refresh and try again.');
      } else if (axiosErr.response?.status === 409) {
        setError('An override already exists for this signatory. Please refresh to view the existing override.');
      } else {
        setError('Failed to save override. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveOverride = async () => {
    setSubmitting(true);
    setError(null);
    setShowRemoveConfirm(false);

    try {
      await deleteOverride(profileId, policyId, signatoryId);
      onSuccess();
      onClose();
    } catch (err) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 404) {
        setError('Override not found. It may have already been removed. Please refresh.');
      } else {
        setError('Failed to remove override. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose} size="medium">
        <Modal.Header title={`Client Detail Override - ${clientName}`} onClose={onClose} />
        <Modal.Content className="policy-override-modal__scrollable">
          {/* Two-column form */}
          <div>
            <FormRow>
              {/* Left column: Name, Email & Address */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <TextInput
                  label="First Name"
                  placeholder={`${clientProfile.firstName} (current)`}
                  value={formState.overrideFirstName || ''}
                  onChange={(e) => handleFieldChange('overrideFirstName', e.target.value)}
                  disabled={submitting}
                  maxLength={100}
                  aria-label="Override first name"
                  fluid
                />
                <TextInput
                  label="Last Name"
                  placeholder={`${clientProfile.lastName} (current)`}
                  value={formState.overrideLastName || ''}
                  onChange={(e) => handleFieldChange('overrideLastName', e.target.value)}
                  disabled={submitting}
                  maxLength={100}
                  aria-label="Override last name"
                  fluid
                />
                <TextInput
                  label="Email"
                  type="email"
                  placeholder={`${clientProfile.email} (current)`}
                  value={formState.overrideEmail || ''}
                  onChange={(e) => handleFieldChange('overrideEmail', e.target.value)}
                  onBlur={handleEmailBlur}
                  disabled={submitting}
                  maxLength={255}
                  aria-label="Override email"
                  error={emailError || undefined}
                  fluid
                />
                <TextInput
                  label="Address Line 1"
                  placeholder={clientProfile.addressLine1 ? `${clientProfile.addressLine1} (current)` : ''}
                  value={formState.overrideAddressLine1 || ''}
                  onChange={(e) => handleFieldChange('overrideAddressLine1', e.target.value)}
                  disabled={submitting}
                  maxLength={255}
                  aria-label="Override address line 1"
                  fluid
                />
                <TextInput
                  label="Address Line 2"
                  placeholder={clientProfile.addressLine2 ? `${clientProfile.addressLine2} (current)` : ''}
                  value={formState.overrideAddressLine2 || ''}
                  onChange={(e) => handleFieldChange('overrideAddressLine2', e.target.value)}
                  disabled={submitting}
                  maxLength={255}
                  aria-label="Override address line 2"
                  fluid
                />
                <TextInput
                  label="Address Line 3"
                  placeholder={clientProfile.addressLine3 ? `${clientProfile.addressLine3} (current)` : ''}
                  value={formState.overrideAddressLine3 || ''}
                  onChange={(e) => handleFieldChange('overrideAddressLine3', e.target.value)}
                  disabled={submitting}
                  maxLength={255}
                  aria-label="Override address line 3"
                  fluid
                />
              </div>

              {/* Right column: Address continued */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <TextInput
                  label="Town / City"
                  placeholder={clientProfile.townCity ? `${clientProfile.townCity} (current)` : ''}
                  value={formState.overrideTownCity || ''}
                  onChange={(e) => handleFieldChange('overrideTownCity', e.target.value)}
                  disabled={submitting}
                  maxLength={100}
                  aria-label="Override town or city"
                  fluid
                />
                <TextInput
                  label="State / Province"
                  placeholder={clientProfile.stateProvinceCounty ? `${clientProfile.stateProvinceCounty} (current)` : ''}
                  value={formState.overrideStateProvinceCounty || ''}
                  onChange={(e) => handleFieldChange('overrideStateProvinceCounty', e.target.value)}
                  disabled={submitting}
                  maxLength={100}
                  aria-label="Override state or province"
                  fluid
                />
                <TextInput
                  label="Postal Code"
                  placeholder={clientProfile.postalCode ? `${clientProfile.postalCode} (current)` : ''}
                  value={formState.overridePostalCode || ''}
                  onChange={(e) => handleFieldChange('overridePostalCode', e.target.value)}
                  disabled={submitting}
                  maxLength={20}
                  aria-label="Override postal code"
                  fluid
                />
                <TextInput
                  label="Country Code"
                  placeholder={clientProfile.countryCode ? `${clientProfile.countryCode} (current)` : ''}
                  value={formState.overrideCountryCode || ''}
                  onChange={(e) => handleFieldChange('overrideCountryCode', e.target.value)}
                  disabled={submitting}
                  maxLength={3}
                  aria-label="Override country code"
                  fluid
                />
                <TextInput
                  label="Country"
                  placeholder={clientProfile.country ? `${clientProfile.country} (current)` : ''}
                  value={formState.overrideCountry || ''}
                  onChange={(e) => handleFieldChange('overrideCountry', e.target.value)}
                  disabled={submitting}
                  maxLength={100}
                  aria-label="Override country"
                  fluid
                />
                <Dropdown
                  label="Gender"
                  placeholder={clientProfile.gender ? `${clientProfile.gender} (current)` : 'Select gender'}
                  options={GENDER_OPTIONS}
                  value={formState.overrideGender ?? ''}
                  onChange={handleGenderChange}
                  disabled={submitting}
                  clearable
                  fluid
                  aria-label="Override gender"
                  data-testid="override-gender-dropdown"
                />
                <Dropdown
                  label="Marital Status"
                  placeholder={clientProfile.maritalStatus ? `${clientProfile.maritalStatus} (current)` : 'Select marital status'}
                  options={MARITAL_STATUS_OPTIONS}
                  value={formState.overrideMaritalStatus ?? ''}
                  onChange={handleMaritalStatusChange}
                  disabled={submitting}
                  clearable
                  fluid
                  aria-label="Override marital status"
                  data-testid="override-marital-status-dropdown"
                />
              </div>
            </FormRow>

            {/* Validation warning: no fields filled */}
            {!hasAnyOverrideField && trimmedReason.length > 0 && (
              <div style={{ marginTop: '1em' }}>
                <StatusBanner
                  variant="error"
                  message='At least one override field must have a value. To remove the override entirely, use the "Remove Override" button.'
                  visible
                  position="inline"
                  dismissible={false}
                />
              </div>
            )}

            {/* Reason field */}
            <div style={{ marginTop: '1em' }}>
              <FinTextArea
                label="Reason (required, 5-1000 characters)"
                placeholder="Explain why these details differ from the client's current profile (e.g., policy was registered under maiden name, previous address on file with provider)..."
                value={formState.reason}
                onChange={handleReasonChange}
                disabled={submitting}
                rows={3}
                aria-label="Reason for override"
                required
                fluid
              />
              <div style={{ textAlign: 'right', fontSize: '0.85em', color: '#999', marginTop: '0.3em' }} aria-live="polite">
                {trimmedReason.length}/1000
                {trimmedReason.length > 0 && trimmedReason.length < 5 && (
                  <span style={{ color: '#db2828' }}>
                    {' '}(minimum 5 characters)
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Error display */}
          {error && (
            <div style={{ marginTop: '1em' }}>
              <StatusBanner
                variant="error"
                message={error}
                visible
                position="inline"
                dismissible={false}
              />
            </div>
          )}
        </Modal.Content>

        <Modal.Actions>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', gap: '0.5em' }}>
              {isEditMode && (
                <Button
                  variant="outlined"
                  colour="red"
                  onClick={() => setShowRemoveConfirm(true)}
                  disabled={submitting}
                  iconLeft={<AppIcon name="trash" size={16} />}
                >
                  Remove Override
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5em' }}>
              <Button variant="outlined" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant="filled"
                onClick={() => { void handleSubmit(); }}
                disabled={isSubmitDisabled}
                loading={submitting}
              >
                {isEditMode ? 'Update Override' : 'Save Override'}
              </Button>
            </div>
          </div>
        </Modal.Actions>
      </Modal>

      {/* Remove confirmation */}
      <Modal open={showRemoveConfirm} onClose={() => setShowRemoveConfirm(false)} size="small" variant="warning">
        <Modal.Header title="Remove Detail Override" onClose={() => setShowRemoveConfirm(false)} />
        <Modal.Content>
          <p>
            Are you sure you want to remove the detail override for {clientName} on policy {policyNumber}? Future LOAs will use the client&apos;s current profile details.
          </p>
        </Modal.Content>
        <Modal.Actions
          secondaryAction={{ label: 'Cancel', onClick: () => setShowRemoveConfirm(false), colour: 'red' }}
          primaryAction={{ label: 'Remove Override', onClick: () => { void handleRemoveOverride(); }, colour: 'red', iconLeft: 'trash' }}
        />
      </Modal>
    </>
  );
}
